import { SelectionError } from '../../selectionError';
import { indexOf } from '../iterable';
import { scrollElementIntoViewIfNeeded } from './element';
import {
  findFirstTextLikeNode,
  findLastTextLikeNode,
  findNearestElement,
  findNextSiblingOrAuncle,
  getEndNode,
  getStartNode,
  isElement,
  isText,
} from './node';
import { isBreakElement } from './paragraph';

export enum PointAffinity {
  START,
  END,
}

interface PointTraversalPredicate {
  (textNode: Text, textOffset: number): boolean;
  (node: Node): boolean;
}

export type PointHoistOptions = {
  /**
   * Condition that must hold for the point's node throughout the hoist.
   *
   * This condition also applies to the ancestor being hoisted up to.
   *
   * It is intended to avoid mutating DOM nodes unnecessarily, e.g. when bolding a substring of text that is
   * already bold.
   */
  condition: (node: Node) => boolean;

  /**
   * Condition that must hold for a node to be split.
   *
   * If the base condition also applies, then the point will hoist up to the node resulting from the split.
   *
   * This is intended to control how far to force the point to be hoisted up. For example, bolding can be done
   * at the text node level, so you would return true for text nodes only. In contrast, removing bold should
   * be done as high up in the flow content element (paragraph) as possible, to split <strong> ancestors, so
   * you would return true for phrasing content elements.
   */
  splitCondition: (node: Node) => boolean;

  /**
   * Whether to prefer a start or end point after a split.
   */
  splitAffinity: PointAffinity;

  /**
   * Condition that must hold for a point at the start of a node to be hoisted up.
   *
   * For example, you may want the node to be the first (non-whitespace) child of its parent.
   */
  startCondition: (node: Node) => boolean;

  /**
   * Condition that must remain true for a point at the end of a node to be hoisted up.
   *
   * For example, you may want the node to be the last (non-whitespace) child of its parent.
   */
  endCondition: (node: Node) => boolean;
};

/**
 * Represents a point in the DOM, i.e. one side of a DOM Range.
 *
 * Use createPointWithOffsetInContainer to create the appropriate subclass.
 *
 * Unlike a DOM Range, these points are stable under DOM mutations. For example, if a node is appended to a new parent,
 * the point will move to the node's new index in that new container.
 */
export abstract class Point {
  protected readonly node: Node;
  protected readonly originalParentNode: Node | null;
  protected readonly originalPreviousSibling: Node | null;
  protected readonly originalNextSibling: Node | null;
  private readonly wasDenormalizedTextNode: boolean = false;
  private readonly originalPrecedingNonTextSibling: Node | null = null;
  private readonly originalPrecedingText: string = '';
  private readonly originalText: string = '';

  constructor(node: Node) {
    // There have been occasional "Cannot read property of undefined" errors for `this.node`, so add this assertion to
    // help debug upstream.
    if (!node) {
      throw new Error(`Cannot create point with node ${node}`);
    }

    this.node = node;

    // Store information for dealing with DOM mutations.
    this.originalParentNode = node.parentNode;
    this.originalPreviousSibling = node.previousSibling;
    this.originalNextSibling = node.nextSibling;

    if (isText(node) && (isText(node.previousSibling) || isText(node.nextSibling))) {
      this.wasDenormalizedTextNode = true;

      let precedingText = '';
      let { previousSibling } = node;
      while (isText(previousSibling)) {
        precedingText += previousSibling.data;
        previousSibling = previousSibling.previousSibling;
      }

      this.originalPrecedingNonTextSibling = previousSibling;
      this.originalPrecedingText = precedingText;
      this.originalText = node.data;
    }
  }

  isValid(): boolean {
    const { ownerDocument: document } = this.node;
    return !!document && document.contains(this.node);
  }

  /** If this point is in the middle of a node, move to its start. */
  moveToStart(): PointAtStartOfNode | this {
    if (!this.isAtEndOfNode(this.node)) {
      return createPointAtStartOfNode(this.node);
    }

    return this;
  }

  /** If this point is in the middle of a node, move to its end. */
  moveToEnd(): PointAtEndOfNode | this {
    if (!this.isAtStartOfNode(this.node)) {
      return createPointAtEndOfNode(this.node);
    }

    return this;
  }

  /** Returns the last point in a depth-first pre-order traversal for which the condition holds. */
  moveForwardWhile(predicate: PointTraversalPredicate, container: Node): Point | this {
    const newPoint = this.moveForwardIf(predicate, container);

    if (newPoint === this) {
      return this;
    }

    return newPoint.moveForwardWhile(predicate, container);
  }

  /** Returns the next point in a depth-first pre-order traversal if the condition holds. */
  abstract moveForwardIf(predicate: PointTraversalPredicate, container: Node): Point | this;

  /**
   * Returns values suitable as arguments for Range#setStart and Range#setEnd.
   *
   * Returns null if the node has since been removed from its document.
   */
  abstract getOffsetInContainer(): [Node, number] | null;

  /**
   * Returns a node (container) and offset pair corrected for DOM normalization.
   *
   * See Node#normalize().
   *
   * @param offset
   *   The offset within the original text node to correct. May be Infinity, which will be interpreted as the
   *   end of the original text node.
   */
  protected getNormalizedOffsetInContainer(offset: number = 0): [Node, number] | null {
    const {
      node,
      wasDenormalizedTextNode,
      originalParentNode,
      originalPrecedingNonTextSibling,
      originalPrecedingText,
      originalText,
    } = this;
    if (
      !wasDenormalizedTextNode ||
      !originalParentNode ||
      // Do nothing if the node is still in the DOM but under a different parent.
      (node.parentNode && node.parentNode !== originalParentNode) ||
      // Do nothing if the node is still denormalized.
      isText(node.previousSibling) ||
      isText(node.nextSibling)
    ) {
      return null;
    }

    const normalizedTextNode = originalPrecedingNonTextSibling
      ? originalPrecedingNonTextSibling.nextSibling
      : originalParentNode.firstChild;
    if (
      !isText(normalizedTextNode) ||
      // Require the text to match exactly to prevent false positives.
      !normalizedTextNode.data.startsWith(originalPrecedingText + originalText)
    ) {
      return null;
    }

    return [normalizedTextNode, originalPrecedingText.length + Math.min(offset, originalText.length)];
  }

  /**
   * Hoists this point up as much as possible while staying on the same node.
   *
   * In practice, this performs a split if the point is in the middle of a node. This is intended to be used
   * to keep a point further down in the tree so that it will remain valid/stable throughout hoisting up
   * higher in the tree.
   */
  abstract hoistSplit(options: PointHoistOptions, container: Node): Point;

  /**
   * Hoists this point up to an ancestor.
   *
   * This performs #hoistSplit() before recursing up the tree.
   *
   * This can be used, for example, to move a point from the start of a text node to the start of its parent
   * container if there are no previous siblings. Or to split an element at the cursor all the way up to the
   * top level container.
   */
  abstract hoist(options: PointHoistOptions, container: Node): Point;

  isAtStartOfNode(node: Node, startCondition: (node: Node) => boolean, container: Node): boolean;
  isAtStartOfNode(node: Node): boolean;
  // eslint-disable-next-line class-methods-use-this
  isAtStartOfNode(): boolean {
    return false;
  }

  isAtEndOfNode(node: Node, endCondition: (node: Node) => boolean, container: Node): boolean;
  isAtEndOfNode(node: Node): boolean;
  // eslint-disable-next-line class-methods-use-this
  isAtEndOfNode(): boolean {
    return false;
  }

  intersectsNode(node: Node) {
    return node.contains(this.node);
  }

  isAfterNode(node: Node): boolean {
    // Check whether this is the same node.
    if (this.isAtEndOfNode(node)) {
      return true;
    }

    const result = this.node.compareDocumentPosition(node);

    // If we are at the end of our node, then we check whether the given node precedes or is a descendant of
    // our node. Otherwise, we only check whether it precedes.
    const mask = this.isAtEndOfNode(this.node)
      ? Node.DOCUMENT_POSITION_PRECEDING | Node.DOCUMENT_POSITION_CONTAINED_BY
      : Node.DOCUMENT_POSITION_PRECEDING;

    // Either way, check wheter the given node STRICTLY precedes, i.e. we are not a descendant of the given
    // node.
    const notMask = Node.DOCUMENT_POSITION_CONTAINS;

    return !!(result & mask) && !(result & notMask);
  }

  isAfterPoint(point: Point): boolean {
    // If the given point is at the start of our node, or if we come after the given point's node, then we definitely
    // follow unless we're equal.
    if (point.isAtStartOfNode(this.node) || this.isAfterNode(point.node)) {
      return !this.isEqual(point);
    }

    // Do the opposite of what we checked for above.
    if (this.isAtStartOfNode(point.node) || point.isAfterNode(this.node)) {
      return false;
    }

    const result = this.node.compareDocumentPosition(point.node);

    // If we are at the end of our node, then we check whether the given node precedes or is a descendant of
    // our node. Otherwise, we only check whether it precedes.
    const mask = this.isAtEndOfNode(this.node)
      ? Node.DOCUMENT_POSITION_PRECEDING | Node.DOCUMENT_POSITION_CONTAINED_BY
      : Node.DOCUMENT_POSITION_PRECEDING;

    return !!(result & mask);
  }

  isEqual(point: Point): boolean {
    if (this.isAtStartOfNode(this.node) && point.isAtStartOfNode(this.node)) {
      return true;
    }

    if (
      this.node.previousSibling &&
      this.isAtStartOfNode(this.node) &&
      point.isAtEndOfNode(this.node.previousSibling)
    ) {
      return true;
    }

    if (this.isAtEndOfNode(this.node) && point.isAtEndOfNode(this.node)) {
      return true;
    }

    if (
      this.node.nextSibling &&
      this.isAtEndOfNode(this.node) &&
      point.isAtStartOfNode(this.node.nextSibling)
    ) {
      return true;
    }

    return false;
  }

  findNearestElement(container: Element): Element;
  findNearestElement(container: Node): Element | null;
  findNearestElement(container: Node): Element | null {
    return findNearestElement(this.node, container);
  }

  /**
   * Finds the next whole node after this point and inside the container.
   *
   * If this point is at the start of a node, it returns that node.
   */
  abstract findNextNode(container: Node): Node | null;

  abstract insertNode(node: Node, returnPointAffinity: PointAffinity.START): PointAtStartOfNode | this;
  abstract insertNode(node: Node, returnPointAffinity: PointAffinity.END): PointAtEndOfNode | this;
  abstract insertNode(node: Node): PointAtEndOfNode | this;
  abstract insertNode(
    node: Node,
    returnPointAffinity?: PointAffinity,
  ): PointAtStartOfNode | PointAtEndOfNode | this;

  scrollIntoViewIfNeeded() {
    const { node } = this;

    // Use the node itself or its parent to scroll into view.
    let element: Element;
    if (isElement(node)) {
      element = node;
    } else if (isElement(node.parentNode)) {
      element = node.parentNode;
    } else {
      return;
    }

    scrollElementIntoViewIfNeeded(element);
  }
}

/**
 * Represent a point within a text node.
 *
 * While it is illegal to create a new PointInText at the start or end of a text node (use PointAtStartOfNode
 * or PointAtEndOfNode instead) it may end up being so if the text node gets mutated after the point has been
 * created.
 */
export class PointInText extends Point {
  readonly text: Text;
  private readonly originalOffset: number;
  private readonly originalDataAfterOffset: string;

  constructor(text: Text, offset: number) {
    super(text);

    if (offset <= 0 || offset >= text.length) {
      throw new Error(
        'Cannot create PointInText with offset before, at the start, at the end, or after the text',
      );
    }

    this.text = text;
    this.originalOffset = offset;
    this.originalDataAfterOffset = text.data.substring(offset);
  }

  get offset(): number {
    const { originalOffset, originalDataAfterOffset, endOffset } = this;

    // If the length didn't change, return the original offset.
    if (originalOffset + originalDataAfterOffset.length === endOffset) {
      return originalOffset;
    }

    // If the length changed and we still end with the same text, correct from the end.
    if (this.text.data.endsWith(originalDataAfterOffset)) {
      return endOffset - originalDataAfterOffset.length;
    }

    return Math.min(originalOffset, endOffset);
  }

  get endOffset(): number {
    return this.text.length;
  }

  getOffsetInContainer(): [Node, number] {
    return this.getNormalizedOffsetInContainer(this.originalOffset) || [this.text, this.offset];
  }

  moveForwardIf(
    predicate: (text: Text, offset: number) => boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _container: Node,
  ): PointAtEndOfNode | this {
    const { text, offset } = this;
    const canMovePastSelf = offset === this.endOffset || predicate(text, offset);
    return canMovePastSelf ? createPointAtEndOfNode(text) : this;
  }

  hoistSplit(options: PointHoistOptions, container: Node): PointAtStartOfNode | PointAtEndOfNode | this {
    const { text, offset } = this;

    // Do what a PointAtStartOfNode would do.
    if (offset === 0) {
      return createPointAtStartOfNode(text).hoistSplit(options, container);
    }

    // Do what a PointAtEndOfNode would do.
    if (offset === this.endOffset) {
      return createPointAtEndOfNode(text).hoistSplit(options, container);
    }

    if (options.splitCondition(text) && options.condition(text) && text.ownerDocument && text.parentNode) {
      if (options.splitAffinity === PointAffinity.END) {
        const splitText = text.splitText(offset);
        text.parentNode.insertBefore(splitText, text.nextSibling);

        return createPointAtEndOfNode(text);
      }

      const textData = text.nodeValue!;
      // eslint-disable-next-line no-param-reassign
      text.nodeValue = textData.substring(offset);

      const splitText = text.ownerDocument.createTextNode(textData.substring(0, offset));
      text.parentNode.insertBefore(splitText, text);

      return createPointAtStartOfNode(text);
    }

    return this;
  }

  hoist(options: PointHoistOptions, container: Node): Point {
    const split = this.hoistSplit(options, container);

    if (split !== this) {
      return split.hoist(options, container);
    }

    return split;
  }

  isAtStartOfNode(node: Node, startCondition: (node: Node) => boolean, container: Node): boolean;
  isAtStartOfNode(node: Node): boolean;
  isAtStartOfNode(node: Node, startCondition?: (node: Node) => boolean, container?: Node): boolean {
    if (startCondition && container) {
      return (
        this.offset === 0 &&
        createPointAtStartOfNode(this.node).isAtStartOfNode(node, startCondition, container)
      );
    }

    return this.offset === 0 && createPointAtStartOfNode(this.node).isAtStartOfNode(node);
  }

  isAtEndOfNode(node: Node, endCondition: (node: Node) => boolean, container: Node): boolean;
  isAtEndOfNode(node: Node): boolean;
  isAtEndOfNode(node: Node, endCondition?: (node: Node) => boolean, container?: Node): boolean {
    if (endCondition && container) {
      return (
        this.offset === this.endOffset &&
        createPointAtEndOfNode(this.node).isAtEndOfNode(node, endCondition, container)
      );
    }

    return this.offset === this.endOffset && createPointAtEndOfNode(this.node).isAtEndOfNode(node);
  }

  isAfterPoint(point: Point): boolean {
    if (point instanceof PointInText && this.node === point.node) {
      return this.offset > point.offset;
    }

    return super.isAfterPoint(point);
  }

  isEqual(point: Point): boolean {
    if (point instanceof PointInText && this.node === point.node) {
      return this.offset === point.offset;
    }

    return super.isEqual(point);
  }

  findNextNode(container: Node) {
    const { text, offset } = this;

    if (offset === 0) {
      return createPointAtStartOfNode(text).findNextNode(container);
    }

    return findNextSiblingOrAuncle(text, container);
  }

  insertNode(
    node: Node,
    returnPointAffinity = PointAffinity.END,
  ): PointAtStartOfNode | PointAtEndOfNode | this {
    const { text, offset } = this;

    // Do what a PointAtStartOfNode would do.
    if (offset === 0) {
      return createPointAtStartOfNode(text).insertNode(node, returnPointAffinity);
    }

    // Do what a PointAtEndOfNode would do.
    if (offset === this.endOffset) {
      return createPointAtEndOfNode(text).insertNode(node, returnPointAffinity);
    }

    const { parentNode, nextSibling } = text;
    const childNode = getChildNodeWithAffinity(node, returnPointAffinity);
    if (!parentNode || !childNode) {
      return this;
    }

    const splitText = text.splitText(offset);
    parentNode.insertBefore(node, nextSibling);
    parentNode.insertBefore(splitText, nextSibling);

    return createPointWithAffinity(childNode, returnPointAffinity);
  }

  toString() {
    const { text, originalOffset } = this;
    return `<PointInText, "${text.data}", ${originalOffset}>`;
  }
}

/**
 * Represents a point immediately before a node.
 */
export class PointAtStartOfNode extends Point {
  getOffsetInContainer(): [Node, number] | null {
    const { node } = this;

    if (isText(node)) {
      const normalizedOffsetInContainer = this.getNormalizedOffsetInContainer();
      if (normalizedOffsetInContainer) {
        return normalizedOffsetInContainer;
      }
    }

    // TODO: Reduce duplication between PointAtStartOfNode#getOffsetInContainer() and PointAtEndOfNode#getOffsetInContainer().
    const { parentNode } = node;
    if (!parentNode) {
      // Try to fallback to the next sibling, if it's still there.
      if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParentNode) {
        return createPointAtStartOfNode(this.originalNextSibling).getOffsetInContainer();
      }

      // Try to fallback to the previous sibling, if it's still there.
      if (
        this.originalPreviousSibling &&
        this.originalPreviousSibling.parentNode === this.originalParentNode
      ) {
        return createPointAtEndOfNode(this.originalPreviousSibling).getOffsetInContainer();
      }

      // Try to fallback to the empty parent.
      if (
        this.originalParentNode &&
        this.originalParentNode.parentNode &&
        !this.originalParentNode.firstChild
      ) {
        return createPointWithOffsetInContainer(this.originalParentNode, 0).getOffsetInContainer();
      }

      return null;
    }

    if (isText(node)) {
      return [node, 0];
    }

    const container = parentNode;
    const index = indexOf(container.childNodes, node);

    return [container, index === -1 ? 0 : index];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  moveForwardIf(predicate: PointTraversalPredicate, _container: Node): PointAtStartOfNode | this {
    const { node } = this;
    const canMovePastSelf = isText(node) ? predicate(node, 0) : predicate(node);
    if (!canMovePastSelf) {
      return this;
    }

    const { firstChild } = node;
    if (firstChild) {
      return createPointAtStartOfNode(firstChild);
    }

    return createPointAtEndOfNode(node);
  }

  hoistSplit(options: PointHoistOptions, container: Node): PointAtStartOfNode {
    const { node } = this;

    if (node.parentNode && !options.splitCondition(node.parentNode)) {
      outerHoistSplit(node.parentNode, options, container);
    }

    if (
      node.parentNode &&
      node.parentNode.parentNode &&
      node.parentNode !== container &&
      options.splitCondition(node.parentNode) &&
      !options.startCondition(node) &&
      options.condition(node)
    ) {
      const { parentNode: grandParentNode } = node.parentNode;
      const nextUncle = node.parentNode;
      const splitParentNode = node.parentNode.cloneNode(false);

      let childNode: Node | null = node.previousSibling;
      while (childNode) {
        const { previousSibling } = childNode;
        splitParentNode.insertBefore(childNode, splitParentNode.firstChild);
        childNode = previousSibling;
      }

      grandParentNode.insertBefore(splitParentNode, nextUncle);
      return createPointAtStartOfNode(node);
    }

    return this;
  }

  hoist(options: PointHoistOptions, container: Node): PointAtStartOfNode {
    const split = this.hoistSplit(options, container);

    if (split !== this) {
      return split.hoist(options, container);
    }

    let { node } = this;

    while (
      node.parentNode &&
      node.parentNode !== container &&
      options.startCondition(node) &&
      options.condition(node) &&
      options.condition(node.parentNode)
    ) {
      node = node.parentNode;
    }

    return this.node === node ? this : createPointAtStartOfNode(node).hoist(options, container);
  }

  isAtStartOfNode(otherNode: Node, startCondition: (node: Node) => boolean, container: Node): boolean;
  isAtStartOfNode(otherNode: Node): boolean;
  isAtStartOfNode(otherNode: Node, startCondition?: (node: Node) => boolean, container?: Node): boolean {
    let { node } = this;

    if (startCondition && container) {
      while (node !== container && node !== otherNode && node.parentNode && startCondition(node)) {
        node = node.parentNode;
      }
    }

    return node === otherNode;
  }

  findNextNode(container: Node) {
    const { node } = this;

    return !container || container.contains(node) ? node : null;
  }

  insertNode(
    newNode: Node,
    returnPointAffinity = PointAffinity.END,
  ): PointAtStartOfNode | PointAtEndOfNode | this {
    const { node } = this;
    const { parentNode } = node;
    const newChildNode = getChildNodeWithAffinity(newNode, returnPointAffinity);
    if (!parentNode || !newChildNode) {
      return this;
    }

    parentNode.insertBefore(newNode, node);
    return createPointWithAffinity(newChildNode, returnPointAffinity);
  }

  toString() {
    const { node } = this;
    return `<PointAtStartOfNode, ${node}>`;
  }
}

/**
 * Represents a point immediately after a node.
 */
export class PointAtEndOfNode extends Point {
  getOffsetInContainer(): [Node, number] | null {
    const { node } = this;

    if (isText(node)) {
      const normalizedOffsetInContainer = this.getNormalizedOffsetInContainer(Infinity);
      if (normalizedOffsetInContainer) {
        return normalizedOffsetInContainer;
      }
    }

    // TODO: Reduce duplication between PointAtStartOfNode#getOffsetInContainer() and PointAtEndOfNode#getOffsetInContainer().
    const { parentNode } = node;
    if (!parentNode) {
      // Try to fallback to the previous sibling, if it's still there.
      if (
        this.originalPreviousSibling &&
        this.originalPreviousSibling.parentNode === this.originalParentNode
      ) {
        return createPointAtEndOfNode(this.originalPreviousSibling).getOffsetInContainer();
      }

      // Try to fallback to the next sibling, if it's still there.
      if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParentNode) {
        return createPointAtStartOfNode(this.originalNextSibling).getOffsetInContainer();
      }

      // Try to fallback to the empty parent.
      if (
        this.originalParentNode &&
        this.originalParentNode.parentNode &&
        !this.originalParentNode.firstChild
      ) {
        return createPointWithOffsetInContainer(this.originalParentNode, 0).getOffsetInContainer();
      }

      return null;
    }

    if (isText(node)) {
      return [node, node.length];
    }

    const container = parentNode;
    const index = indexOf(container.childNodes, node);

    return [container, index === -1 ? container.childNodes.length : index + 1];
  }

  moveForwardIf(
    _predicate: PointTraversalPredicate,
    container: Node,
  ): PointAtStartOfNode | PointAtEndOfNode | this {
    const { node } = this;
    const { parentNode, nextSibling } = node;

    if (nextSibling) {
      return createPointAtStartOfNode(nextSibling);
    }

    if (parentNode && parentNode !== container) {
      return createPointAtEndOfNode(parentNode);
    }

    return this;
  }

  hoistSplit(options: PointHoistOptions, container: Node): PointAtEndOfNode {
    const { node } = this;

    if (node.parentNode && !options.splitCondition(node.parentNode)) {
      outerHoistSplit(node.parentNode, options, container);
    }

    if (
      node.parentNode &&
      node.parentNode.parentNode &&
      node.parentNode !== container &&
      options.splitCondition(node.parentNode) &&
      !options.endCondition(node) &&
      options.condition(node)
    ) {
      const { parentNode: grandParentNode, nextSibling: nextUncle } = node.parentNode;
      const splitParentNode = node.parentNode.cloneNode(false);

      let childNode: Node | null = node.nextSibling;
      while (childNode) {
        const { nextSibling } = childNode;
        splitParentNode.appendChild(childNode);
        childNode = nextSibling;
      }

      grandParentNode.insertBefore(splitParentNode, nextUncle);
      return createPointAtEndOfNode(node);
    }

    return this;
  }

  hoist(options: PointHoistOptions, container: Node): PointAtEndOfNode {
    const split = this.hoistSplit(options, container);

    if (split !== this) {
      return split.hoist(options, container);
    }

    let { node } = this;

    while (
      node.parentNode &&
      node.parentNode !== container &&
      options.endCondition(node) &&
      options.condition(node) &&
      options.condition(node.parentNode)
    ) {
      node = node.parentNode;
    }

    return this.node === node ? this : createPointAtEndOfNode(node).hoist(options, container);
  }

  isAtEndOfNode(otherNode: Node, endCondition: (node: Node) => boolean, container: Node): boolean;
  isAtEndOfNode(otherNode: Node): boolean;
  isAtEndOfNode(otherNode: Node, endCondition?: (node: Node) => boolean, container?: Node): boolean {
    let { node } = this;

    if (endCondition && container) {
      while (node !== container && node !== otherNode && node.parentNode && endCondition(node)) {
        node = node.parentNode;
      }
    }

    return node === otherNode;
  }

  findNextNode(container: Node): Node | null {
    return findNextSiblingOrAuncle(this.node, container);
  }

  insertNode(
    newNode: Node,
    returnPointAffinity = PointAffinity.END,
  ): PointAtStartOfNode | PointAtEndOfNode | this {
    const { node } = this;
    const { parentNode } = node;
    const newChildNode = getChildNodeWithAffinity(newNode, returnPointAffinity);
    if (!parentNode || !newChildNode) {
      return this;
    }

    parentNode.insertBefore(newNode, node.nextSibling);
    return createPointWithAffinity(newChildNode, returnPointAffinity);
  }

  toString() {
    const { node } = this;
    return `<PointAtEndOfNode, ${node}>`;
  }
}

/**
 * Represent a point in an empty element.
 *
 * If an element becomes non-empty, the point becames equivalent to a point at the start of the first child.
 */
export class PointInLeaf extends Point {
  constructor(node: Node) {
    if (node.firstChild) {
      throw new Error('Cannot create PointInLeaf with non-empty node');
    }

    super(node);
  }

  moveToStart(): PointAtStartOfNode | this {
    const { node } = this;

    // Do what a PointAtStartOfNode would do if we became non-empty.
    if (node.firstChild) {
      return createPointAtStartOfNode(node.firstChild).moveToStart();
    }

    return this;
  }

  moveToEnd(): PointAtEndOfNode | this {
    const { node } = this;

    // Do what a PointAtStartOfNode would do if we became non-empty.
    if (node.firstChild) {
      return createPointAtStartOfNode(node.firstChild).moveToEnd();
    }

    return this;
  }

  moveForwardIf(_predicate: PointTraversalPredicate, container: Node): PointAtStartOfNode | PointAtEndOfNode {
    const { node } = this;

    const { firstChild } = node;
    if (firstChild) {
      return createPointAtStartOfNode(firstChild);
    }

    if (node !== container) {
      return createPointAtEndOfNode(node);
    }

    return this;
  }

  getOffsetInContainer(): [Node, number] {
    return [this.node, 0];
  }

  hoistSplit(options: PointHoistOptions, container: Node): PointAtStartOfNode | this {
    const { node } = this;

    // Do what a PointAtStartOfNode would do if we became non-empty.
    if (node.firstChild) {
      return createPointAtStartOfNode(node.firstChild).hoistSplit(options, container);
    }

    if (!options.splitCondition(node)) {
      outerHoistSplit(node, options, container);
    }

    if (node.parentNode && node !== container && options.splitCondition(node) && options.condition(node)) {
      const { parentNode } = node;
      const splitNode = node.cloneNode(false);
      parentNode.insertBefore(splitNode, node);
      return createPointAtStartOfNode(node);
    }

    return this;
  }

  hoist(options: PointHoistOptions, container: Node): Point {
    const split = this.hoistSplit(options, container);

    if (split !== this) {
      return split.hoist(options, container);
    }

    return split;
  }

  isAtStartOfNode(targetNode: Node, startCondition: (node: Node) => boolean, container: Node): boolean;
  isAtStartOfNode(targetNode: Node): boolean;
  isAtStartOfNode(targetNode: Node, startCondition?: (node: Node) => boolean, container?: Node): boolean {
    const { node } = this;

    // Do what a PointAtStartOfNode would do if we became non-empty.
    if (node.firstChild) {
      const point = createPointAtStartOfNode(node.firstChild);
      return startCondition && container
        ? point.isAtStartOfNode(targetNode, startCondition, container)
        : point.isAtStartOfNode(targetNode);
    }

    return false;
  }

  isAtEndOfNode(targetNode: Node, endCondition: (node: Node) => boolean, container: Node): boolean;
  isAtEndOfNode(targetNode: Node): boolean;
  isAtEndOfNode(targetNode: Node, endCondition?: (node: Node) => boolean, container?: Node): boolean {
    const { node } = this;

    // Do what a PointAtStartOfNode would do if we became non-empty.
    if (node.firstChild) {
      const point = createPointAtStartOfNode(node.firstChild);
      return endCondition && container
        ? point.isAtEndOfNode(targetNode, endCondition, container)
        : point.isAtEndOfNode(targetNode);
    }

    return false;
  }

  isEqual(point: Point): boolean {
    const { node } = this;

    // Do what a PointAtStartOfNode would do if we became non-empty.
    if (node.firstChild) {
      return createPointAtStartOfNode(node.firstChild).isEqual(point);
    }

    return point instanceof PointInLeaf && point.node === node;
  }

  findNextNode(container: Node) {
    const { node } = this;
    return node.firstChild || createPointAtEndOfNode(node).findNextNode(container);
  }

  insertNode(
    newNode: Node,
    returnPointAffinity = PointAffinity.END,
  ): PointAtStartOfNode | PointAtEndOfNode | this {
    const { node } = this;
    const newChildNode = getChildNodeWithAffinity(newNode, returnPointAffinity);
    if (!newChildNode) {
      return this;
    }

    node.insertBefore(newNode, node.firstChild);
    return createPointWithAffinity(newChildNode, returnPointAffinity);
  }

  toString() {
    const { node } = this;
    return `<PointInLeaf, ${node}>`;
  }
}

export function createRangeWithPoint(document: Document, point: Point): Range {
  return updateRangeWithPoints(document.createRange(), point, point);
}

export function createRangeWithPoints(document: Document, startPoint: Point, endPoint: Point): Range {
  return updateRangeWithPoints(document.createRange(), startPoint, endPoint);
}

export function cloneRangeWithPoint(otherRange: Range, point: Point): Range {
  return updateRangeWithPoints(otherRange.cloneRange(), point, point);
}

export function cloneRangeWithPoints(otherRange: Range, startPoint: Point, endPoint: Point): Range {
  return updateRangeWithPoints(otherRange.cloneRange(), startPoint, endPoint);
}

export function updateRangeWithPoints(range: Range, startPoint: Point, endPoint: Point): Range {
  setStartOfRangeWithPoint(range, startPoint);
  setEndOfRangeWithPoint(range, endPoint);
  return range;
}

export function setStartOfRangeWithPoint(range: Range, startPoint: Point): Range {
  const offsetInContainer = startPoint.getOffsetInContainer();
  if (!offsetInContainer) {
    throw new SelectionError('Failed to set start of range, node has been removed from document');
  }

  range.setStart(...offsetInContainer);
  return range;
}

export function setEndOfRangeWithPoint(range: Range, endPoint: Point): Range {
  const offsetInContainer = endPoint.getOffsetInContainer();
  if (!offsetInContainer) {
    throw new SelectionError('Failed to set end of range, node has been removed from document');
  }

  range.setEnd(...offsetInContainer);
  return range;
}

export function createPointWithStartOfRange(range: Range) {
  return createPointWithOffsetInContainer(range.startContainer, range.startOffset);
}

export function createPointWithEndOfRange(range: Range) {
  return createPointWithOffsetInContainer(range.endContainer, range.endOffset);
}

export function updateSelectionWithPoints(selection: Selection, anchorPoint: Point, focusPoint: Point) {
  setAnchorInSelectionWithPoint(selection, anchorPoint);
  setFocusInSelectionWithPoint(selection, focusPoint);
}

export function setAnchorInSelectionWithPoint(selection: Selection, point: Point) {
  const offsetInContainer = point.getOffsetInContainer();
  if (!offsetInContainer) {
    throw new SelectionError('Failed to set anchor in selection, node has been removed from document');
  }

  const [node, offset] = offsetInContainer;
  if (selection.anchorNode === node && selection.anchorOffset === offset) {
    return;
  }

  selection.collapse(node, offset);
}

export function setFocusInSelectionWithPoint(selection: Selection, point: Point) {
  const offsetInContainer = point.getOffsetInContainer();
  if (!offsetInContainer) {
    throw new SelectionError('Failed to set focus in selection, node has been removed from document');
  }

  const [node, offset] = offsetInContainer;
  if (selection.focusNode === node && selection.focusOffset === offset) {
    return;
  }

  selection.extend(node, offset);
}

export function createPointWithOffsetInContainer(container: Node, offset: number): Point {
  if (isText(container)) {
    if (offset <= 0) {
      return new PointAtStartOfNode(container);
    }

    if (offset < container.length) {
      return new PointInText(container, offset);
    }

    return new PointAtEndOfNode(container);
  }

  const { childNodes } = container;

  if (childNodes.length === 0) {
    return new PointInLeaf(container);
  }

  if (offset < childNodes.length) {
    return new PointAtStartOfNode(childNodes[offset]);
  }

  return new PointAtEndOfNode(childNodes[childNodes.length - 1]);
}

export function isPointInText(point: Point): point is PointInText {
  return point instanceof PointInText;
}

export function createPointAtStartOfNode(node: Node): PointAtStartOfNode {
  return new PointAtStartOfNode(node);
}

export function createPointAtEndOfNode(node: Node): PointAtEndOfNode {
  return new PointAtEndOfNode(node);
}

export function createPointAtStartOfNodeContents(node: Node): PointAtStartOfNode | PointInLeaf {
  return createPointWithOffsetInContainer(node, 0);
}

export function createPointAtEndOfNodeContents(node: Node): PointAtEndOfNode | PointInLeaf {
  return createPointWithOffsetInContainer(node, Infinity);
}

export function findFirstTextPoint(container: Node, startNode?: Node): Point | undefined {
  const node = findFirstTextLikeNode(container, startNode);
  return node ? createPointAtStartOfNode(node) : undefined;
}

export function findLastTextPoint(
  container: Node,
  startNode?: Node,
  maxIterations = Infinity,
): Point | undefined {
  const node = findLastTextLikeNode(container, startNode, maxIterations);

  if (isText(node)) {
    return createPointAtEndOfNode(node);
  }

  if (isBreakElement(node)) {
    return createPointAtStartOfNode(node);
  }

  return undefined;
}

/**
 * If possible, split the node's parent at the start and end of the node, and move the split parent inside the node.
 */
function outerHoistSplit(node: Node, options: PointHoistOptions, container: Node) {
  if (
    !node.parentNode ||
    !node.parentNode.parentNode ||
    node.parentNode === container ||
    !options.splitCondition(node.parentNode) ||
    !options.condition(node)
  ) {
    return;
  }

  // Split the parent at the end of the node.
  const afterNode = createPointAtEndOfNode(node);
  const afterNodeHoisted = afterNode.hoist({ ...options, splitAffinity: PointAffinity.END }, container);

  // This should have succeeded based on the earlier conditions, but make sure.
  if (afterNodeHoisted.isAtEndOfNode(node)) {
    return;
  }

  // Split the parent of the start of the node, and move the second half inside the node.
  const { parentNode: grandParentNode } = node.parentNode;
  const { nextSibling: nextUncle } = node.parentNode;

  const splitParentNode = node.parentNode.cloneNode(false);

  let childNode: Node | null = node.firstChild;
  while (childNode) {
    const { nextSibling } = childNode;
    splitParentNode.appendChild(childNode);
    childNode = nextSibling;
  }

  node.appendChild(splitParentNode);
  grandParentNode.insertBefore(node, nextUncle);
}

function getChildNodeWithAffinity(node: Node, affinity: PointAffinity): (Node & ChildNode) | null {
  return affinity === PointAffinity.START ? getStartNode(node) : getEndNode(node);
}

function createPointWithAffinity(
  node: Exclude<Node, DocumentFragment>,
  affinity: PointAffinity.START,
): PointAtStartOfNode;
function createPointWithAffinity(
  node: Exclude<Node, DocumentFragment>,
  affinity: PointAffinity.END,
): PointAtEndOfNode;
function createPointWithAffinity(
  node: Exclude<Node, DocumentFragment>,
  affinity: PointAffinity,
): PointAtStartOfNode | PointAtEndOfNode;
function createPointWithAffinity(
  node: Exclude<Node, DocumentFragment>,
  affinity: PointAffinity,
): PointAtStartOfNode | PointAtEndOfNode {
  return affinity === PointAffinity.START ? createPointAtStartOfNode(node) : createPointAtEndOfNode(node);
}
